// Keep identical to the backend's begin-connect check.
export const ALLOWED_TENANT_ORIGIN = /^https:\/\/(?:(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+cnected\.app|(?:[a-z0-9]+-)?employer\.cnected\.com)$/

const STATE_TOKEN = /^[0-9a-f]{96}$/

export interface OAuthConnectTarget {
  redirectUri: string
  tenantOrigin?: string
}

export type OAuthCallbackAction = { kind: 'complete' } | { kind: 'relay'; url: string } | { kind: 'reject' }

type Here = Pick<Location, 'origin'>

// Real path, never `/#/`: RFC 6749 §3.1.2 forbids a fragment in a redirect URI, and providers reject one.
function callbackUrl(origin: string, channelId: string): string {
  return `${origin}/auth/oauth/callback/${channelId}`
}

export function isAllowedTenantOrigin(origin: string): boolean {
  return ALLOWED_TENANT_ORIGIN.test(origin)
}

export function oauthConnectTarget(channelId: string, here: Here, canonicalHost: string): OAuthConnectTarget {
  if (!isAllowedTenantOrigin(here.origin)) return { redirectUri: callbackUrl(here.origin, channelId) }
  return { redirectUri: callbackUrl(`https://${canonicalHost}`, channelId), tenantOrigin: here.origin }
}

/** undefined: no origin in the state; null: an invalid token or an origin that does not decode. */
export function tenantOriginFromState(state: string | null | undefined): string | null | undefined {
  if (!state?.includes('.')) return undefined
  if (!STATE_TOKEN.test(state.slice(0, state.indexOf('.')))) return null
  // Unpadded base64url; atob reads only padded standard base64.
  const tail = state
    .slice(state.indexOf('.') + 1)
    .replace(/-/g, '+')
    .replace(/_/g, '/')
  try {
    return atob(tail.padEnd(Math.ceil(tail.length / 4) * 4, '='))
  } catch {
    return null
  }
}

export function oauthCallbackAction(channelId: string, state: string | null | undefined, query: URLSearchParams, here: Here): OAuthCallbackAction {
  const origin = tenantOriginFromState(state)
  // A relayed page decodes its own origin, so it completes: no loop.
  if (origin === undefined || origin === here.origin) return { kind: 'complete' }
  if (origin === null || !isAllowedTenantOrigin(origin)) return { kind: 'reject' }
  return { kind: 'relay', url: `${callbackUrl(origin, channelId)}?${query.toString()}` }
}
